<template>
  <iframe ref="iframeRef" :src="editorUrl" class="pops-host-iframe" frameborder="0" @load="onIframeLoad" />
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { PopsHost, createApiRequestHandler } from '@prospective/pops-host';
import type { PopsHostConfig } from '@prospective/pops-host';
import type { PopsState, PatchOperation, FormQuestion, APIInteraction } from '@prospective/pops-shared';
import { applyPatch } from 'fast-json-patch';
import type { IFormQuestion } from '@/types/formQuestion';
import type { FormContactMethod } from '@/types/publication';
import { transformPopsProxyRequest } from './popsProxyRequest';
import { isFormPreviewRequest, mockFormResponse } from './formPreviewMock';

const formTypeMap: Record<string, FormQuestion['type']> = {
  text: 'textarea',
  textarea: 'textarea',
  radio: 'radio',
  select: 'select',
  checkbox: 'checkbox',
  toggle: 'checkbox',
};

function mapFormQuestions(questions: IFormQuestion[]): FormQuestion[] {
  return questions.map((q) => ({
    id: q.id,
    type: formTypeMap[q.type] ?? 'textarea',
    name: q.name,
    label: q.label,
    description: q.description ?? '',
    hint: q.hint,
    icon: q.icon,
    placeholder: q.placeholder,
    required: q.required,
    group: q.group,
    sort_order: q.sort_order,
    options: q.options as any,
  }));
}

const props = defineProps<{
  templateKey: string;
  variation?: string;
  mode: 'editor' | 'preview';
  readonly?: boolean;
  options: Record<string, unknown>;
  publication?: PopsState['publication'];
  formQuestions?: IFormQuestion[];
  contactMethod?: FormContactMethod;
  interactionModules?: Record<string, any> | null;
  parameters?: Record<string, unknown>;
  /** language pops renders the template's own texts in, i.e. the order's language */
  locale?: string;
}>();

const emit = defineEmits<{
  (e: 'update:options', value: Record<string, unknown>): void;
  (e: 'client-patch', patches: PatchOperation[], newState: PopsState): void;
}>();

const popsBaseUrl = (import.meta.env.VITE_POPS_BASE_URL ?? 'http://localhost:8015').replace(/\/$/, '');
const popsOrigin = new URL(popsBaseUrl).origin;

const iframeRef = ref<HTMLIFrameElement | null>(null);
let host: PopsHost | null = null;
let suppressOptionsWatch = false;
const isConnected = ref(false);
let reloadCounter = 0;

// changing the variation or locale changes the url, which reloads the iframe so pops re-renders it
const editorUrl = computed(() => {
  if (!props.templateKey) return '';

  const query = new URLSearchParams();
  if (props.variation) query.set('variation', props.variation);
  if (props.locale) query.set('locale', props.locale);
  const queryString = query.toString();

  return `${popsBaseUrl}/v1/templates/${props.templateKey}/editor` + (queryString ? `?${queryString}` : '');
});

const realApiRequestHandler = createApiRequestHandler({
  transformRequest: transformPopsProxyRequest,
});

// in preview mode, intercept funnel-flow calls so the recruiter can walk through all
// funnel steps without any real submission, OTP send, or EC-DataVault write
function apiRequestHandler(req: Parameters<NonNullable<PopsHostConfig['onApiRequest']>>[0]) {
  if (props.mode === 'preview' && isFormPreviewRequest(req)) {
    return Promise.resolve(mockFormResponse(req));
  }
  return (realApiRequestHandler as NonNullable<PopsHostConfig['onApiRequest']>)(req);
}

function destroyHost() {
  if (host) {
    host.destroy();
    host = null;
    isConnected.value = false;
  }
}

function buildState(): PopsState {
  const state: PopsState = {
    options: props.options,
    // render the LEAD preview as POPS 'prod' so the template enables interactive elements
    // (e.g. the funnel apply CTA uses @click="!isPreview && startFunnel()" and only
    // renders the funnel button when isProd); all funnel API calls are intercepted by
    // apiRequestHandler in preview mode, so nothing real reaches the backend.
    // when readonly is set, keep 'preview' so the template's isPreview guard disables CTAs
    mode: !props.readonly && props.mode === 'preview' ? 'prod' : props.mode,
    publication: props.publication,
    // the interaction modules are fetched over the network, so the template often renders
    // before they land. ready: false tells it a missing module means "not yet" rather than
    // "unsupported", so it holds its loading state instead of committing to a placeholder —
    // an image option set at boot would otherwise flash its placeholder until it resolves.
    //
    // only null means "expected, still loading": a caller that never passes the prop (the
    // template step, which previews demo data) has nothing to wait for, and reporting it as
    // not-ready would leave the template loading forever. always present, so the patch below
    // can replace it.
    ready: props.interactionModules !== null,
  };

  if (props.parameters) {
    state.parameters = props.parameters;
  }

  if (props.interactionModules) {
    state.modules = { ...props.interactionModules };
  }

  if (props.formQuestions) {
    state.modules = state.modules || {};
    // preserve the real funnel/form actions from interactionModules so the apply CTA is functional;
    // fall back to empty only when there are no modules yet (non-funnel publications keep actions empty)
    const existingFunnelActions =
      (props.interactionModules?.funnel as { actions?: Record<string, APIInteraction> } | undefined)?.actions ?? {};
    const existingFormActions =
      (props.interactionModules?.form as { actions?: Record<string, APIInteraction> } | undefined)?.actions ??
      existingFunnelActions;
    const mappedQuestions = mapFormQuestions(props.formQuestions);
    const contactMethod = props.contactMethod ?? 'email';
    // funnel_enabled: true tells the template to render the funnel button instead of the plain link;
    // only true when real actions have been loaded (i.e. this is a funnel-enabled publication)
    const funnelEnabled = Object.keys(existingFunnelActions).length > 0;

    // modules.form dual-serves modules.funnel with identical shared fields - see
    // FormModuleStateProvider on the POPS side. Populating both here (not just funnel) is what
    // keeps a later `replace /modules/form/questions` patch from failing with
    // OPERATION_PATH_UNRESOLVABLE: that op requires the path to already exist.
    state.modules.funnel = {
      ...state.modules.funnel,
      actions: existingFunnelActions,
      funnel_enabled: funnelEnabled,
      contact_method: contactMethod,
      questions: mappedQuestions,
    } as any;
    state.modules.form = {
      ...state.modules.form,
      actions: existingFormActions,
      funnel_enabled: funnelEnabled,
      contact_method: contactMethod,
      questions: mappedQuestions,
    } as any;
  }

  return JSON.parse(JSON.stringify(state));
}

function onIframeLoad() {
  destroyHost();

  const iframe = iframeRef.value;
  if (!iframe || !editorUrl.value) return;

  host = new PopsHost({
    iframe,
    targetOrigin: popsOrigin,
    initialState: buildState(),
    onApiRequest: apiRequestHandler,
    onClientPatch(_mutationId: string, patches: PatchOperation[], newState: PopsState) {
      suppressOptionsWatch = true;
      try {
        // apply against a full state clone so patches outside /options are not lost
        const stateClone = JSON.parse(JSON.stringify(buildState()));
        applyPatch(stateClone, structuredClone(patches) as any, false, true);
        emit('update:options', stateClone.options ?? {});
      } catch {
        if (newState.options) {
          emit('update:options', { ...newState.options });
        }
      }
      emit('client-patch', patches, newState);
    },
  });

  host.connect();
  isConnected.value = true;
}

watch(
  () => props.mode,
  (m) => {
    if (host) {
      host.patchState([{ op: 'replace', path: '/mode', value: m === 'preview' ? 'prod' : m }]);
    }
  }
);

watch(
  () => props.options,
  (opts) => {
    if (!host) return;
    if (suppressOptionsWatch) {
      suppressOptionsWatch = false;
      return;
    }
    host.patchState([{ op: 'replace', path: '/options', value: JSON.parse(JSON.stringify(opts ?? {})) }]);
  },
  { deep: true }
);

watch(
  () => props.publication,
  (pub, oldPub) => {
    if (!host) return;
    if (pub === undefined) {
      if (oldPub) host.patchState([{ op: 'remove', path: '/publication' }]);
      return;
    }
    const op = oldPub ? 'replace' : 'add';
    host.patchState([{ op, path: '/publication', value: JSON.parse(JSON.stringify(pub)) }]);
  }
);

watch(
  () => props.interactionModules,
  (modules) => {
    if (!host) return;
    // back to loading: the parent clears the modules before refetching them, and until the
    // new set lands a missing module means "not yet" again
    if (!modules) {
      host.patchState([{ op: 'replace', path: '/ready', value: false }]);
      return;
    }
    const merged = { ...modules };
    if (props.formQuestions) {
      const funnelActions = (modules.funnel as { actions?: Record<string, APIInteraction> } | undefined)?.actions ?? {};
      const formActions =
        (modules.form as { actions?: Record<string, APIInteraction> } | undefined)?.actions ?? funnelActions;
      const mappedQuestions = mapFormQuestions(props.formQuestions);
      const contactMethod = props.contactMethod ?? 'email';
      const funnelEnabled = Object.keys(funnelActions).length > 0;

      // modules.form dual-serves modules.funnel here too - see the matching comment in
      // buildState() for why both need the same fields
      merged.funnel = {
        ...merged.funnel,
        actions: funnelActions,
        funnel_enabled: funnelEnabled,
        contact_method: contactMethod,
        questions: mappedQuestions,
      } as any;
      merged.form = {
        ...merged.form,
        actions: formActions,
        funnel_enabled: funnelEnabled,
        contact_method: contactMethod,
        questions: mappedQuestions,
      } as any;
    }
    // the template owns modules.images.cache (its resolved image urls live there), and this
    // fetch usually lands after the first render — replacing /modules wholesale would drop
    // every already-resolved image, so carry the cache over from the current host state
    const imagesCache = (host.getState().modules?.images as { cache?: unknown } | undefined)?.cache;
    if (imagesCache) {
      merged.images = { ...(merged.images as Record<string, unknown> | undefined), cache: imagesCache };
    }
    host.patchState([
      { op: 'replace', path: '/modules', value: JSON.parse(JSON.stringify(merged)) },
      { op: 'replace', path: '/ready', value: true },
    ]);
  }
);

watch(
  () => props.formQuestions,
  (qs) => {
    if (!host || !qs) return;
    const mapped = JSON.parse(JSON.stringify(mapFormQuestions(qs)));
    // both replace ops always land: buildState()/the interactionModules watcher above always
    // populate modules.form and modules.funnel together, so neither path is ever missing here
    host.patchState([
      { op: 'replace', path: '/modules/funnel/questions', value: mapped },
      { op: 'replace', path: '/modules/form/questions', value: mapped },
    ]);
  }
);

// reflect the recruiter's contact-method setting into the live funnel preview;
// the template only shows the phone field and the preferred-contact-time step
// when the method includes phone, so this drives both
watch(
  () => props.contactMethod,
  (method) => {
    if (!host || !props.formQuestions) return;
    host.patchState([
      { op: 'replace', path: '/modules/funnel/contact_method', value: method ?? 'email' },
      { op: 'replace', path: '/modules/form/contact_method', value: method ?? 'email' },
    ]);
  }
);

function patchOption(key: string, value: unknown) {
  if (!host) return;
  host.patchState([{ op: 'replace', path: `/options/${key}`, value: JSON.parse(JSON.stringify(value)) }]);
}

function reload() {
  const iframe = iframeRef.value;
  if (!iframe || !editorUrl.value) return;
  destroyHost();
  // append a counter so the browser always fires the load event even when the URL is unchanged
  const sep = editorUrl.value.includes('?') ? '&' : '?';
  iframe.src = `${editorUrl.value}${sep}_r=${++reloadCounter}`;
}

function getState(): PopsState {
  return buildState();
}

onBeforeUnmount(() => {
  destroyHost();
});

function emitToTemplate(name: string, payload?: unknown) {
  host?.emit(name, payload);
}

defineExpose({ patchOption, reload, getState, isConnected, emitToTemplate });
</script>

<style scoped>
.pops-host-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
